<template>
  <dashboard-layout title="Subscription Cancellers">
    <template #actions>
      <div class="tabs">
        <button :class="['tab-btn', { active: activeTab === 'table' }]" @click="activeTab = 'table'">Table</button>
        <button :class="['tab-btn', { active: activeTab === 'graph' }]" @click="activeTab = 'graph'">Graph</button>
      </div>
      <div class="range-presets">
        <button
          v-for="opt in rangePresets"
          :key="opt.value"
          :class="['range-btn', { active: isPresetActive(opt.value) }]"
          @click="applyRangePreset(opt.value)"
          :title="opt.label + ' (cancel date)'"
        >{{ opt.label }}</button>
        <span v-if="isCustomRangeActive" class="range-custom-indicator" :title="`Custom: ${cancelFrom || '*'} → ${cancelTo || '*'}`">Custom</span>
      </div>
      <search-box v-model="searchQuery" placeholder="Search user id…" />
      <button v-if="anyFilterActive" @click="clearAllFilters" class="clear-btn">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
        Clear filters
      </button>
      <button @click="exportCsv" class="export-btn" :disabled="filteredLifecycles.length === 0">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        Export ({{ filteredLifecycles.length }})
      </button>
    </template>

    <stat-grid>
      <stat-card label="Total" :value="filteredLifecycles.length.toLocaleString('en-US')" icon="userx" accent />
      <stat-card label="Subscription" :value="subCount.toLocaleString('en-US')" icon="creditcard" />
      <stat-card label="Trial" :value="trialCount.toLocaleString('en-US')" icon="zap" />
      <stat-card label="Repurchased" :value="repurchasedCount.toLocaleString('en-US')" icon="activity" />
    </stat-grid>

    <data-panel v-if="activeTab === 'table'" flush>
      <template #title>Cancellations <span class="muted">({{ filteredLifecycles.length.toLocaleString('en-US') }})</span></template>

      <div v-if="loading" class="loading-state">
        <div class="loader-steps">
          <div class="loader-step" :class="stepClass('events')">
            <div class="step-icon">
              <div v-if="phases.events.status === 'loading'" class="cosmic-loader sm"></div>
              <svg v-else-if="phases.events.status === 'done'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              <span v-else class="step-dot"></span>
            </div>
            <span class="step-label">Loading subscription events</span>
            <span v-if="phases.events.status === 'done'" class="step-count">{{ phases.events.count.toLocaleString() }}</span>
          </div>
          <div class="loader-step" :class="stepClass('feedbacks')">
            <div class="step-icon">
              <div v-if="phases.feedbacks.status === 'loading'" class="cosmic-loader sm"></div>
              <svg v-else-if="phases.feedbacks.status === 'done'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              <span v-else class="step-dot"></span>
            </div>
            <span class="step-label">Loading feedbacks</span>
            <span v-if="phases.feedbacks.status === 'done'" class="step-count">{{ phases.feedbacks.count.toLocaleString() }}</span>
          </div>
          <div class="loader-step" :class="stepClass('processing')">
            <div class="step-icon">
              <div v-if="phases.processing.status === 'loading'" class="cosmic-loader sm"></div>
              <svg v-else-if="phases.processing.status === 'done'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              <span v-else class="step-dot"></span>
            </div>
            <span class="step-label">Building lifecycles</span>
            <span v-if="phases.processing.status === 'done'" class="step-count">{{ phases.processing.count.toLocaleString() }}</span>
          </div>
        </div>
      </div>

      <div v-else class="table-scroll-wrapper">
        <table class="lifecycle-table">
          <thead>
            <tr>
              <th></th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>User ID</span>
                  <button @click.stop="toggleFilter('userId')" :class="['filter-icon-btn', { 'has-filter': !!userIdFilter }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="userIdFilter" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'userId'" class="filter-dropdown" @click.stop>
                    <input type="text" v-model="userIdFilter" placeholder="Filter by user id..." class="filter-search-input">
                    <div class="filter-actions">
                      <button @click="userIdFilter = ''">Clear</button>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Type</span>
                  <button @click.stop="toggleFilter('type')" :class="['filter-icon-btn', { 'has-filter': typeFilter !== 'all' }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="typeFilter !== 'all'" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'type'" class="filter-dropdown" @click.stop>
                    <div class="filter-options">
                      <label class="filter-option"><input type="radio" :checked="typeFilter === 'all'" @change="typeFilter = 'all'; activeFilter = null"><span>All</span></label>
                      <label class="filter-option"><input type="radio" :checked="typeFilter === 'subscription'" @change="typeFilter = 'subscription'; activeFilter = null"><span>Subscription</span></label>
                      <label class="filter-option"><input type="radio" :checked="typeFilter === 'trial'" @change="typeFilter = 'trial'; activeFilter = null"><span>Trial</span></label>
                      <label class="filter-option"><input type="radio" :checked="typeFilter === 'unknown'" @change="typeFilter = 'unknown'; activeFilter = null"><span>Unknown</span></label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Env</span>
                  <button @click.stop="toggleFilter('environment')" :class="['filter-icon-btn', { 'has-filter': selectedEnvironments.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="selectedEnvironments.length > 0" class="filter-badge">{{ selectedEnvironments.length }}</span>
                  </button>
                  <div v-if="activeFilter === 'environment'" class="filter-dropdown" @click.stop>
                    <div class="filter-actions">
                      <button @click="selectedEnvironments = [...uniqueEnvironments]">Select All</button>
                      <button @click="selectedEnvironments = []">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="value in uniqueEnvironments" :key="value" class="filter-option">
                        <input type="checkbox" :checked="selectedEnvironments.includes(value)" @change="toggleEnvironment(value)">
                        <span>{{ value }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Start</span>
                  <button @click.stop="toggleFilter('start')" :class="['filter-icon-btn', { 'has-filter': startFrom || startTo }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="startFrom || startTo" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'start'" class="filter-dropdown filter-dropdown-date" @click.stop>
                    <div class="date-range-field"><label>From</label><input type="date" v-model="startFrom" class="date-input"></div>
                    <div class="date-range-field"><label>To</label><input type="date" v-model="startTo" class="date-input"></div>
                    <div class="filter-actions"><button @click="startFrom = ''; startTo = ''">Clear</button></div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Cancel</span>
                  <button @click.stop="toggleFilter('cancel')" :class="['filter-icon-btn', { 'has-filter': cancelFrom || cancelTo }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="cancelFrom || cancelTo" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'cancel'" class="filter-dropdown filter-dropdown-date" @click.stop>
                    <div class="date-range-field"><label>From</label><input type="date" v-model="cancelFrom" class="date-input"></div>
                    <div class="date-range-field"><label>To</label><input type="date" v-model="cancelTo" class="date-input"></div>
                    <div class="filter-actions"><button @click="cancelFrom = ''; cancelTo = ''">Clear</button></div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Duration</span>
                  <button @click.stop="toggleFilter('duration')" :class="['filter-icon-btn', { 'has-filter': durationMin !== '' || durationMax !== '' }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="durationMin !== '' || durationMax !== ''" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'duration'" class="filter-dropdown filter-dropdown-date" @click.stop>
                    <div class="date-range-field"><label>Min (days)</label><input type="number" min="0" v-model="durationMin" class="date-input"></div>
                    <div class="date-range-field"><label>Max (days)</label><input type="number" min="0" v-model="durationMax" class="date-input"></div>
                    <div class="filter-actions"><button @click="durationMin = ''; durationMax = ''">Clear</button></div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Package</span>
                  <button @click.stop="toggleFilter('package')" :class="['filter-icon-btn', { 'has-filter': selectedPackages.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="selectedPackages.length > 0" class="filter-badge">{{ selectedPackages.length }}</span>
                  </button>
                  <div v-if="activeFilter === 'package'" class="filter-dropdown" @click.stop>
                    <div class="filter-actions">
                      <button @click="selectedPackages = [...uniquePackages]">Select All</button>
                      <button @click="selectedPackages = []">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="value in uniquePackages" :key="value" class="filter-option">
                        <input type="checkbox" :checked="selectedPackages.includes(value)" @change="togglePackage(value)">
                        <span>{{ value }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Feedback</span>
                  <button @click.stop="toggleFilter('feedback')" :class="['filter-icon-btn', { 'has-filter': feedbackFilter !== 'all' }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="feedbackFilter !== 'all'" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'feedback'" class="filter-dropdown filter-dropdown-right" @click.stop>
                    <div class="filter-options">
                      <label class="filter-option"><input type="radio" :checked="feedbackFilter === 'yes'" @change="feedbackFilter = 'yes'; activeFilter = null"><span>Yes</span></label>
                      <label class="filter-option"><input type="radio" :checked="feedbackFilter === 'no'" @change="feedbackFilter = 'no'; activeFilter = null"><span>No</span></label>
                      <label class="filter-option"><input type="radio" :checked="feedbackFilter === 'all'" @change="feedbackFilter = 'all'; activeFilter = null"><span>All</span></label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Repurchased</span>
                  <button @click.stop="toggleFilter('repurchased')" :class="['filter-icon-btn', { 'has-filter': repurchasedFilter !== 'all' }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="repurchasedFilter !== 'all'" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'repurchased'" class="filter-dropdown filter-dropdown-right" @click.stop>
                    <div class="filter-options">
                      <label class="filter-option"><input type="radio" :checked="repurchasedFilter === 'yes'" @change="repurchasedFilter = 'yes'; activeFilter = null"><span>Yes</span></label>
                      <label class="filter-option"><input type="radio" :checked="repurchasedFilter === 'no'" @change="repurchasedFilter = 'no'; activeFilter = null"><span>No</span></label>
                      <label class="filter-option"><input type="radio" :checked="repurchasedFilter === 'all'" @change="repurchasedFilter = 'all'; activeFilter = null"><span>All</span></label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Repurchase Pkg</span>
                  <button @click.stop="toggleFilter('repurchasePackage')" :class="['filter-icon-btn', { 'has-filter': selectedRepurchasePackages.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="selectedRepurchasePackages.length > 0" class="filter-badge">{{ selectedRepurchasePackages.length }}</span>
                  </button>
                  <div v-if="activeFilter === 'repurchasePackage'" class="filter-dropdown filter-dropdown-right" @click.stop>
                    <div class="filter-actions">
                      <button @click="selectedRepurchasePackages = [...uniqueRepurchasePackages]">Select All</button>
                      <button @click="selectedRepurchasePackages = []">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="value in uniqueRepurchasePackages" :key="value" class="filter-option">
                        <input type="checkbox" :checked="selectedRepurchasePackages.includes(value)" @change="toggleRepurchasePackage(value)">
                        <span>{{ value }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Repurchase Date</span>
                  <button @click.stop="toggleFilter('repurchaseDate')" :class="['filter-icon-btn', { 'has-filter': repurchaseDateFrom || repurchaseDateTo }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="repurchaseDateFrom || repurchaseDateTo" class="filter-badge">•</span>
                  </button>
                  <div v-if="activeFilter === 'repurchaseDate'" class="filter-dropdown filter-dropdown-date filter-dropdown-right" @click.stop>
                    <div class="date-range-field"><label>From</label><input type="date" v-model="repurchaseDateFrom" class="date-input"></div>
                    <div class="date-range-field"><label>To</label><input type="date" v-model="repurchaseDateTo" class="date-input"></div>
                    <div class="filter-actions"><button @click="repurchaseDateFrom = ''; repurchaseDateTo = ''">Clear</button></div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, idx) in filteredLifecycles" :key="idx">
              <tr :class="['lifecycle-row', { 'feedback-only': row.feedbackOnly }]">
                <td class="expand-cell">
                  <button v-if="row.feedback" @click="toggleExpand(idx)" class="expand-btn" :class="{ expanded: expanded[idx] }">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </td>
                <td>
                  <router-link :to="`/user/${row.userId}`" class="user-link">{{ truncate(row.userId, 16) }}</router-link>
                  <span v-if="row.feedbackOnly" class="fb-only-badge" title="No adapty cancel event found — built from feedback timestamp">feedback only</span>
                </td>
                <td>
                  <status-badge :variant="typeVariant(row.cancelType)" :label="typeLabel(row.cancelType)" />
                </td>
                <td>
                  <status-badge :variant="envVariant(row.environment)" :label="row.environment || 'Unknown'" />
                </td>
                <td>{{ formatDate(row.startDateMs) }}</td>
                <td>{{ formatDate(row.cancelDateMs) }}</td>
                <td>{{ formatDuration(row.durationMs) }}</td>
                <td>
                  <span class="pkg-tag" :title="row.vendorProductId || ''">{{ row.planType }}</span>
                </td>
                <td>
                  <status-badge :variant="row.feedback ? 'success' : 'neutral'" :label="row.feedback ? 'Yes' : 'No'" />
                </td>
                <td>
                  <status-badge :variant="row.repurchased ? 'success' : 'neutral'" :label="row.repurchased ? 'Yes' : 'No'" />
                </td>
                <td>
                  <span v-if="row.repurchasePlanType" class="pkg-tag" :title="row.repurchasePackage || ''">{{ row.repurchasePlanType }}</span>
                  <span v-else class="muted">-</span>
                </td>
                <td>
                  <span v-if="row.repurchaseDateMs">{{ formatDate(row.repurchaseDateMs) }}</span>
                  <span v-else class="muted">-</span>
                </td>
              </tr>
              <tr v-if="expanded[idx] && row.feedback" class="feedback-row">
                <td></td>
                <td colspan="11">
                  <div class="feedback-detail">
                    <div v-if="row.feedback.mainReason" class="fb-item">
                      <span class="fb-label">Main Reason</span>
                      <span class="fb-value">{{ row.feedback.mainReason }}</span>
                    </div>
                    <div v-if="row.feedback.detailedFeedback" class="fb-item">
                      <span class="fb-label">Detailed Feedback</span>
                      <span class="fb-value">{{ row.feedback.detailedFeedback }}</span>
                    </div>
                    <div v-if="row.feedback.additionalNote" class="fb-item">
                      <span class="fb-label">Additional Note</span>
                      <span class="fb-value">{{ row.feedback.additionalNote }}</span>
                    </div>
                    <div v-if="row.feedback.feedback" class="fb-item">
                      <span class="fb-label">Feedback</span>
                      <span class="fb-value">{{ row.feedback.feedback }}</span>
                    </div>
                    <router-link :to="`/feedback/${row.feedback.id}`" class="fb-link">Open feedback →</router-link>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="filteredLifecycles.length === 0">
              <td colspan="12" class="no-data">No cancellation lifecycles found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </data-panel>

    <data-panel v-if="activeTab === 'graph'" title="Avg Duration (hours)">
      <loading-state v-if="loading" label="Loading…" />
      <template v-else>
        <div class="graph-controls">
          <div class="control-group">
            <span class="control-label">Granularity</span>
            <div class="segmented">
              <button :class="['seg-btn', { active: granularity === 'daily' }]" @click="granularity = 'daily'">Daily</button>
              <button :class="['seg-btn', { active: granularity === 'weekly' }]" @click="granularity = 'weekly'">Weekly</button>
              <button :class="['seg-btn', { active: granularity === 'monthly' }]" @click="granularity = 'monthly'">Monthly</button>
            </div>
          </div>
          <div class="control-group">
            <label class="toggle-label">
              <input type="checkbox" v-model="useTrailingAverage" class="toggle-checkbox">
              <span class="toggle-text">Use trailing average</span>
            </label>
            <div class="info-tooltip">
              <svg class="info-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span class="tooltip-text">Each data point averages durations over a 7-{{ granularity === 'daily' ? 'day' : granularity === 'weekly' ? 'week' : 'month' }} trailing window. Smooths short-term volatility.</span>
            </div>
          </div>
          <div class="control-group" v-if="graphChartData.length > 0">
            <span class="stat-pill-graph">
              <span class="stat-label">Overall avg</span>
              <span class="stat-value">{{ overallAvgDuration }}</span>
            </span>
            <span class="stat-pill-graph">
              <span class="stat-label">Buckets</span>
              <span class="stat-value">{{ graphChartData.length }}</span>
            </span>
            <span class="stat-pill-graph">
              <span class="stat-label">Cancels</span>
              <span class="stat-value">{{ graphTotalCancels }}</span>
            </span>
          </div>
        </div>
        <div v-if="graphChartData.length === 0" class="no-data">No data to chart for current filters</div>
        <div v-else class="chart-wrap">
          <LineChart
            :data="displayGraphData"
            :buildNumbers="[]"
            title="Avg Duration (hours)"
            color="#a78bfa"
            unit="h"
          />
        </div>
      </template>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { FirebaseSubscriptionLifecycleService } from '../services/firebaseSubscriptionLifecycleService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import SearchBox from './ui/SearchBox.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LineChart from './LineChart.vue'

export default {
  name: 'SubscriptionCancellersDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, SearchBox, LoadingState, StatusBadge, LineChart
  },
  setup() {
    const service = new FirebaseSubscriptionLifecycleService()
    const lifecycles = ref([])
    const loading = ref(true)
    const searchQuery = ref('')
    const expanded = ref({})
    const phases = ref({
      events: { status: 'pending', count: 0 },
      feedbacks: { status: 'pending', count: 0 },
      processing: { status: 'pending', count: 0 }
    })

    const activeTab = ref('table')
    const granularity = ref('daily')
    const useTrailingAverage = ref(false)

    const activeFilter = ref(null)
    const userIdFilter = ref('')
    const startFrom = ref('')
    const startTo = ref('')
    const cancelFrom = ref('')
    const cancelTo = ref('')
    const durationMin = ref('')
    const durationMax = ref('')
    const selectedPackages = ref([])
    const selectedRepurchasePackages = ref([])
    const selectedEnvironments = ref([])
    const feedbackFilter = ref('all')
    const repurchasedFilter = ref('all')
    const typeFilter = ref('all')
    const repurchaseDateFrom = ref('')
    const repurchaseDateTo = ref('')

    const toggleFilter = (key) => {
      activeFilter.value = activeFilter.value === key ? null : key
    }

    const togglePackage = (value) => {
      const idx = selectedPackages.value.indexOf(value)
      if (idx > -1) selectedPackages.value.splice(idx, 1)
      else selectedPackages.value.push(value)
    }

    const toggleRepurchasePackage = (value) => {
      const idx = selectedRepurchasePackages.value.indexOf(value)
      if (idx > -1) selectedRepurchasePackages.value.splice(idx, 1)
      else selectedRepurchasePackages.value.push(value)
    }

    const toggleEnvironment = (value) => {
      const idx = selectedEnvironments.value.indexOf(value)
      if (idx > -1) selectedEnvironments.value.splice(idx, 1)
      else selectedEnvironments.value.push(value)
    }

    const uniquePackages = computed(() => {
      const set = new Set(lifecycles.value.map(r => r.planType).filter(Boolean))
      return [...set].sort()
    })

    const uniqueRepurchasePackages = computed(() => {
      const set = new Set(lifecycles.value.map(r => r.repurchasePlanType).filter(Boolean))
      return [...set].sort()
    })

    const uniqueEnvironments = computed(() => {
      const set = new Set(lifecycles.value.map(r => r.environment).filter(Boolean))
      return [...set].sort()
    })

    const anyFilterActive = computed(() =>
      !!searchQuery.value || !!userIdFilter.value ||
      !!startFrom.value || !!startTo.value ||
      !!cancelFrom.value || !!cancelTo.value ||
      durationMin.value !== '' || durationMax.value !== '' ||
      selectedPackages.value.length > 0 || selectedRepurchasePackages.value.length > 0 ||
      selectedEnvironments.value.length > 0 ||
      feedbackFilter.value !== 'all' || repurchasedFilter.value !== 'all' ||
      typeFilter.value !== 'all' ||
      !!repurchaseDateFrom.value || !!repurchaseDateTo.value
    )

    const clearAllFilters = () => {
      searchQuery.value = ''
      userIdFilter.value = ''
      startFrom.value = ''
      startTo.value = ''
      cancelFrom.value = ''
      cancelTo.value = ''
      durationMin.value = ''
      durationMax.value = ''
      selectedPackages.value = []
      selectedRepurchasePackages.value = []
      selectedEnvironments.value = []
      feedbackFilter.value = 'all'
      repurchasedFilter.value = 'all'
      typeFilter.value = 'all'
      repurchaseDateFrom.value = ''
      repurchaseDateTo.value = ''
    }

    const closeDropdowns = (e) => {
      if (!e.target.closest('.filter-dropdown') && !e.target.closest('.filter-icon-btn')) {
        activeFilter.value = null
      }
    }

    const rangePresets = [
      { value: 'all', label: 'All' },
      { value: '7d', label: 'Last 7d' },
      { value: '30d', label: 'Last 30d' },
      { value: '90d', label: 'Last 90d' }
    ]

    const toIsoDate = (d) => d.toISOString().slice(0, 10)

    const computePresetRange = (preset) => {
      if (preset === 'all') return { from: '', to: '' }
      const daysMap = { '7d': 7, '30d': 30, '90d': 90 }
      const days = daysMap[preset]
      if (!days) return null
      const today = new Date()
      const past = new Date()
      past.setDate(today.getDate() - days + 1)
      return { from: toIsoDate(past), to: toIsoDate(today) }
    }

    const isPresetActive = (preset) => {
      const target = computePresetRange(preset)
      if (!target) return false
      return cancelFrom.value === target.from && cancelTo.value === target.to
    }

    const isCustomRangeActive = computed(() => {
      if (!cancelFrom.value && !cancelTo.value) return false
      return !rangePresets.some(opt => isPresetActive(opt.value))
    })

    const applyRangePreset = (preset) => {
      const target = computePresetRange(preset)
      if (!target) return
      cancelFrom.value = target.from
      cancelTo.value = target.to
    }

    const stepClass = (key) => ({
      'is-loading': phases.value[key].status === 'loading',
      'is-done': phases.value[key].status === 'done'
    })

    const load = async () => {
      try {
        const onProgress = ({ phase, status, count }) => {
          if (status === 'start') {
            phases.value[phase].status = 'loading'
          } else if (status === 'done') {
            phases.value[phase].status = 'done'
            phases.value[phase].count = count ?? 0
          }
        }
        lifecycles.value = await service.getCancellationLifecycles(onProgress)
      } catch (e) {
        console.error('Error loading cancellation lifecycles:', e)
      } finally {
        loading.value = false
      }
    }

    const inDateRange = (ms, fromStr, toStr) => {
      if (!fromStr && !toStr) return true
      if (!ms) return false
      if (fromStr) {
        const from = new Date(fromStr)
        from.setHours(0, 0, 0, 0)
        if (ms < from.getTime()) return false
      }
      if (toStr) {
        const to = new Date(toStr)
        to.setHours(23, 59, 59, 999)
        if (ms > to.getTime()) return false
      }
      return true
    }

    const filteredLifecycles = computed(() => {
      const q = searchQuery.value.trim().toLowerCase()
      const userQ = userIdFilter.value.trim().toLowerCase()
      const minDays = durationMin.value === '' ? null : Number(durationMin.value)
      const maxDays = durationMax.value === '' ? null : Number(durationMax.value)

      return lifecycles.value.filter(r => {
        if (q && !r.userId.toLowerCase().includes(q)) return false
        if (userQ && !r.userId.toLowerCase().includes(userQ)) return false

        if (!inDateRange(r.startDateMs, startFrom.value, startTo.value)) return false
        if (!inDateRange(r.cancelDateMs, cancelFrom.value, cancelTo.value)) return false

        if (minDays != null || maxDays != null) {
          if (r.durationMs == null) return false
          const days = r.durationMs / 86400000
          if (minDays != null && days < minDays) return false
          if (maxDays != null && days > maxDays) return false
        }

        if (selectedPackages.value.length > 0 && !selectedPackages.value.includes(r.planType)) return false

        if (selectedEnvironments.value.length > 0 && !selectedEnvironments.value.includes(r.environment)) return false

        if (feedbackFilter.value === 'yes' && !r.feedback) return false
        if (feedbackFilter.value === 'no' && r.feedback) return false

        if (repurchasedFilter.value === 'yes' && !r.repurchased) return false
        if (repurchasedFilter.value === 'no' && r.repurchased) return false

        if (typeFilter.value !== 'all' && (r.cancelType || 'unknown') !== typeFilter.value) return false

        if (selectedRepurchasePackages.value.length > 0) {
          if (!r.repurchasePlanType || !selectedRepurchasePackages.value.includes(r.repurchasePlanType)) return false
        }

        if (repurchaseDateFrom.value || repurchaseDateTo.value) {
          if (!inDateRange(r.repurchaseDateMs, repurchaseDateFrom.value, repurchaseDateTo.value)) return false
        }

        return true
      })
    })

    const repurchasedCount = computed(() =>
      filteredLifecycles.value.filter(r => r.repurchased).length
    )
    const subCount = computed(() =>
      filteredLifecycles.value.filter(r => r.cancelType === 'subscription').length
    )
    const trialCount = computed(() =>
      filteredLifecycles.value.filter(r => r.cancelType === 'trial').length
    )

    const bucketKey = (ms, granVal) => {
      const d = new Date(ms)
      if (granVal === 'monthly') {
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
      }
      if (granVal === 'weekly') {
        const day = d.getUTCDay() || 7
        const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day + 1))
        return monday.toISOString().slice(0, 10)
      }
      return d.toISOString().slice(0, 10)
    }

    const formatBucketLabel = (key, granVal) => {
      if (granVal === 'monthly') {
        const [y, m] = key.split('-')
        return new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' })
      }
      if (granVal === 'weekly') {
        const d = new Date(key)
        return d.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })
      }
      const d = new Date(key)
      return d.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })
    }

    const nextBucket = (key, granVal) => {
      if (granVal === 'monthly') {
        const [y, m] = key.split('-').map(Number)
        const next = new Date(Date.UTC(y, m, 1))
        return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`
      }
      const d = new Date(key)
      d.setUTCDate(d.getUTCDate() + (granVal === 'weekly' ? 7 : 1))
      return d.toISOString().slice(0, 10)
    }

    // Returns array of { key, label, durations: number[] } sorted ascending and densified (no gaps)
    const graphChartData = computed(() => {
      const granVal = granularity.value
      const buckets = new Map()
      for (const r of filteredLifecycles.value) {
        if (!r.cancelDateMs || r.durationMs == null) continue
        const key = bucketKey(r.cancelDateMs, granVal)
        if (!buckets.has(key)) buckets.set(key, [])
        buckets.get(key).push(r.durationMs / 3600000)
      }
      if (buckets.size === 0) return []

      const sortedKeys = [...buckets.keys()].sort()
      // Densify between min and max
      const dense = []
      let cur = sortedKeys[0]
      const last = sortedKeys[sortedKeys.length - 1]
      while (cur <= last) {
        const durations = buckets.get(cur) || []
        const avg = durations.length
          ? durations.reduce((s, v) => s + v, 0) / durations.length
          : 0
        dense.push({
          key: cur,
          date: formatBucketLabel(cur, granVal),
          value: parseFloat(avg.toFixed(2)),
          durations,
          count: durations.length
        })
        cur = nextBucket(cur, granVal)
        if (dense.length > 5000) break // safety
      }
      return dense
    })

    const displayGraphData = computed(() => {
      if (!useTrailingAverage.value || graphChartData.value.length === 0) {
        return graphChartData.value
      }
      const W = 7
      return graphChartData.value.map((item, idx) => {
        const start = Math.max(0, idx - W + 1)
        const window = graphChartData.value.slice(start, idx + 1)
        const allDurations = window.flatMap(d => d.durations)
        const avg = allDurations.length
          ? allDurations.reduce((s, v) => s + v, 0) / allDurations.length
          : 0
        return {
          ...item,
          value: parseFloat(avg.toFixed(2)),
          count: allDurations.length
        }
      })
    })

    const overallAvgDuration = computed(() => {
      const all = graphChartData.value.flatMap(b => b.durations)
      if (all.length === 0) return '-'
      const avg = all.reduce((s, v) => s + v, 0) / all.length
      return `${avg.toFixed(1)}h`
    })

    const graphTotalCancels = computed(() =>
      graphChartData.value.reduce((s, b) => s + b.count, 0)
    )

    const toggleExpand = (idx) => {
      expanded.value = { ...expanded.value, [idx]: !expanded.value[idx] }
    }

    const formatDate = (ms) => {
      if (!ms) return '-'
      return new Date(ms).toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatDuration = (ms) => {
      if (ms == null) return '-'
      if (ms <= 0) return '0m'
      const seconds = Math.floor(ms / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)
      if (days > 0) {
        const remHours = hours % 24
        return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`
      }
      if (hours > 0) {
        const remMin = minutes % 60
        return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`
      }
      if (minutes > 0) return `${minutes}m`
      return `${seconds}s`
    }

    const truncate = (text, n) => {
      if (!text) return '-'
      return text.length > n ? text.substring(0, n) + '…' : text
    }

    const envVariant = (env) => ({
      production: 'success',
      sandbox: 'danger'
    }[(env || '').toLowerCase()] || 'neutral')

    const typeVariant = (t) => ({
      subscription: 'info',
      trial: 'warning'
    }[t] || 'neutral')
    const typeLabel = (t) => ({
      subscription: 'Subscription',
      trial: 'Trial'
    }[t] || 'Unknown')

    const exportCsv = () => {
      const headers = [
        'User ID', 'Type', 'Environment', 'Start Date', 'Cancel Date', 'Duration',
        'Package', 'Vendor Product ID', 'Has Feedback', 'Main Reason',
        'Detailed Feedback', 'Additional Note', 'Feedback Text',
        'Repurchased', 'Repurchase Package', 'Repurchase Date'
      ]
      const rows = filteredLifecycles.value.map(r => [
        r.userId,
        typeLabel(r.cancelType),
        r.environment || 'Unknown',
        formatDate(r.startDateMs),
        formatDate(r.cancelDateMs),
        formatDuration(r.durationMs),
        r.planType,
        r.vendorProductId || '',
        r.feedback ? 'Yes' : 'No',
        r.feedback?.mainReason || '',
        r.feedback?.detailedFeedback || '',
        r.feedback?.additionalNote || '',
        r.feedback?.feedback || '',
        r.repurchased ? 'Yes' : 'No',
        r.repurchasePackage || '',
        r.repurchaseDateMs ? formatDate(r.repurchaseDateMs) : ''
      ])
      const csv = [headers, ...rows]
        .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n')

      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `subscription_cancellers_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    }

    onMounted(() => {
      load()
      document.addEventListener('click', closeDropdowns)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', closeDropdowns)
    })

    return {
      lifecycles,
      filteredLifecycles,
      loading,
      phases,
      stepClass,
      searchQuery,
      expanded,
      repurchasedCount,
      subCount,
      trialCount,
      toggleExpand,
      formatDate,
      formatDuration,
      truncate,
      envVariant,
      typeVariant,
      typeLabel,
      exportCsv,
      activeFilter,
      toggleFilter,
      userIdFilter,
      startFrom,
      startTo,
      cancelFrom,
      cancelTo,
      durationMin,
      durationMax,
      selectedPackages,
      selectedRepurchasePackages,
      selectedEnvironments,
      uniquePackages,
      uniqueRepurchasePackages,
      uniqueEnvironments,
      togglePackage,
      toggleRepurchasePackage,
      toggleEnvironment,
      feedbackFilter,
      repurchasedFilter,
      typeFilter,
      repurchaseDateFrom,
      repurchaseDateTo,
      anyFilterActive,
      clearAllFilters,
      activeTab,
      granularity,
      useTrailingAverage,
      graphChartData,
      displayGraphData,
      overallAvgDuration,
      graphTotalCancels,
      rangePresets,
      isPresetActive,
      isCustomRangeActive,
      applyRangePreset
    }
  }
}
</script>

<style scoped>
.muted {
  color: var(--text-secondary);
  opacity: 0.5;
}
.tabs {
  display: flex;
  gap: 0;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 3px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.tab-btn {
  padding: 6px 16px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}
.tab-btn:hover:not(.active) {
  color: var(--text-primary);
}
.tab-btn.active {
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  color: white;
}
.range-presets {
  display: flex;
  gap: 4px;
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  padding: 3px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.range-presets .range-btn {
  padding: 5px 12px;
  background: transparent;
  border: none;
  border-radius: 5px;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.range-presets .range-btn:hover:not(.active) {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.05);
}
.range-presets .range-btn.active {
  background: var(--primary-color);
  color: white;
}
.range-custom-indicator {
  font-size: 11px;
  padding: 4px 9px;
  margin-left: 4px;
  background: rgba(108, 99, 255, 0.18);
  border-radius: 5px;
  color: #a78bfa;
  font-weight: 500;
  cursor: help;
}
.export-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border: 1px solid #22c55e;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
}
.export-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}
.export-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.clear-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #f87171;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.clear-btn:hover {
  background: rgba(239, 68, 68, 0.18);
}
.filterable-header {
  position: relative;
}
.header-filter-container {
  display: flex;
  align-items: center;
  gap: 6px;
}
.filter-icon-btn {
  display: flex;
  align-items: center;
  gap: 2px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 3px 5px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.15s ease;
  position: relative;
}
.filter-icon-btn:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.08);
}
.filter-icon-btn.has-filter {
  color: var(--primary-color);
  background: rgba(108, 99, 255, 0.15);
  border-color: rgba(108, 99, 255, 0.3);
}
.filter-badge {
  font-size: 9px;
  font-weight: 700;
  background: var(--primary-color);
  color: white;
  border-radius: 50%;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.filter-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 220px;
  max-width: 320px;
  background: rgba(26, 27, 46, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 10px;
  z-index: 100;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
}
.filter-dropdown-right {
  left: auto;
  right: 0;
  min-width: 160px;
}
.filter-dropdown-date {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.filter-search-input {
  width: 100%;
  padding: 7px 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 12px;
  margin-bottom: 8px;
  box-sizing: border-box;
}
.filter-search-input:focus {
  outline: none;
  border-color: var(--primary-color);
}
.filter-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.filter-actions button {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 11px;
  cursor: pointer;
  padding: 2px 0;
  font-weight: 500;
}
.filter-actions button:hover {
  text-decoration: underline;
}
.filter-options {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: var(--text-primary);
  transition: background 0.15s ease;
  text-transform: none;
  letter-spacing: normal;
}
.filter-option:hover {
  background: rgba(255, 255, 255, 0.06);
}
.filter-option input {
  accent-color: var(--primary-color);
  cursor: pointer;
}
.date-range-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.date-range-field label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}
.date-input {
  padding: 7px 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 12px;
  font-family: inherit;
  color-scheme: dark;
  text-transform: none;
}
.date-input:focus {
  outline: none;
  border-color: var(--primary-color);
}
.table-scroll-wrapper {
  overflow-x: auto;
}
.lifecycle-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.lifecycle-table th,
.lifecycle-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
}
.lifecycle-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(26, 27, 46, 0.98);
}
.lifecycle-row:hover {
  background: rgba(255, 255, 255, 0.03);
}
.lifecycle-row.feedback-only {
  background: rgba(251, 191, 36, 0.04);
}
.lifecycle-row.feedback-only:hover {
  background: rgba(251, 191, 36, 0.08);
}
.fb-only-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  border-radius: 4px;
  cursor: help;
  vertical-align: middle;
}
.expand-cell {
  width: 32px;
  padding-right: 0 !important;
}
.expand-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.expand-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-primary);
}
.expand-btn svg {
  transition: transform 0.2s ease;
}
.expand-btn.expanded svg {
  transform: rotate(90deg);
}
.user-link {
  color: var(--primary-color);
  text-decoration: none;
  font-family: monospace;
  font-size: 12px;
}
.user-link:hover {
  text-decoration: underline;
}
.pkg-tag {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(108, 99, 255, 0.12);
  color: #a78bfa;
  white-space: nowrap;
}
.feedback-row {
  background: rgba(108, 99, 255, 0.04);
}
.feedback-detail {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 4px;
}
.fb-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fb-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}
.fb-value {
  color: var(--text-primary);
  font-size: 13px;
  white-space: pre-wrap;
}
.fb-link {
  align-self: flex-start;
  margin-top: 4px;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 12px;
}
.fb-link:hover {
  text-decoration: underline;
}
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 16px;
  color: var(--text-secondary);
}
.cosmic-loader {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(108, 99, 255, 0.3);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
}
.cosmic-loader.sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
}
.loader-steps {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  min-width: 320px;
}
.loader-step {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--text-secondary);
  opacity: 0.5;
  transition: opacity 0.2s ease;
}
.loader-step.is-loading,
.loader-step.is-done {
  opacity: 1;
}
.loader-step.is-done {
  color: #34d399;
}
.step-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}
.step-label {
  flex: 1;
}
.step-count {
  font-family: monospace;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 12px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.no-data {
  text-align: center;
  color: var(--text-secondary);
  padding: 32px !important;
}
.graph-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
}
.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.control-label {
  font-size: 11px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.segmented {
  display: flex;
  background: rgba(255, 255, 255, 0.04);
  padding: 3px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.seg-btn {
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}
.seg-btn:hover:not(.active) {
  color: var(--text-primary);
}
.seg-btn.active {
  background: var(--primary-color);
  color: white;
}
.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.toggle-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--primary-color);
}
.toggle-text {
  font-size: 12px;
  color: var(--text-secondary);
}
.toggle-label:hover .toggle-text {
  color: var(--text-primary);
}
.info-tooltip {
  position: relative;
  display: flex;
  align-items: center;
}
.info-icon {
  color: var(--text-secondary);
  cursor: help;
}
.info-icon:hover {
  color: var(--primary-color);
}
.tooltip-text {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 260px;
  padding: 10px 12px;
  background: #2c2d42;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #e0e0e0;
  text-align: left;
  z-index: 100;
  transition: opacity 0.15s, visibility 0.15s;
  text-transform: none;
  letter-spacing: normal;
}
.info-tooltip:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
.stat-pill-graph {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: rgba(108, 99, 255, 0.1);
  border: 1px solid rgba(108, 99, 255, 0.25);
  border-radius: 6px;
  font-size: 12px;
}
.stat-pill-graph .stat-label {
  color: var(--text-secondary);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.stat-pill-graph .stat-value {
  color: #a78bfa;
  font-weight: 600;
}
.chart-wrap {
  min-height: 360px;
  position: relative;
}
</style>
